/** App shell: app bars, status strip, banners, bottom tab bar and the desktop sidebar. */

import type { ReactNode } from 'react';
import { useAuth } from '../app/auth-context.js';
import { usePanel, useStore } from '../app/context.js';
import { Link, type Route } from '../app/router.js';
import { agentCounts, counts, outboxWaiting, systemHealth } from '../app/selectors.js';
import { setupSummary } from '../app/settings.js';
import { nextThemePreference, setThemePreference, THEME_LABELS, useThemePreference } from '../app/theme.js';
import { formatClock, formatDayMonth, formatLongDate, formatSecondsLeft } from '../lib/format.js';
import { KillSwitch, POLICY_HOLD_MS, useHold } from './HoldButton.js';
import { Icon, type IconName, LogoTile } from './icons.js';
import { LiveDot, TierBadge } from './primitives.js';

export type TabId = 'bugun' | 'onaylar' | 'istekler' | 'ekip' | 'diger';

/** Same calendar day in the viewer's zone. */
function sameDay(a: number, b: number): boolean {
  const x = new Date(a);
  const y = new Date(b);
  return x.getFullYear() === y.getFullYear() && x.getMonth() === y.getMonth() && x.getDate() === y.getDate();
}

export function activeTab(route: Route): TabId {
  switch (route.name) {
    case 'bugun':
      return 'bugun';
    case 'onaylar':
    case 'onay':
      return 'onaylar';
    case 'istekler':
      return 'istekler';
    case 'ekip':
    case 'kisi':
      return 'ekip';
    default:
      return 'diger';
  }
}

/** Kill switch wired to the store: hold completes the hard stop, early release explains. */
export function ConnectedKillSwitch({ variant }: { variant: 'compact' | 'desktop' }) {
  const store = useStore();
  const { team } = usePanel();
  return (
    <KillSwitch
      variant={variant}
      stopped={team.mode === 'stopped'}
      onStop={() => void store.emergencyStop()}
      onEarlyRelease={() => store.showToast('Durdurmak için 2 sn basılı tut', { icon: 'info' })}
    />
  );
}

/** Phone app bar for the five tab screens. */
export function AppBar({ title, subtitle, logo = false }: { title: string; subtitle: string; logo?: boolean }) {
  return (
    <header className="appbar">
      {logo ? (
        <span className="appbar__logo">
          <LogoTile />
        </span>
      ) : null}
      <div className="appbar__titles">
        <div className="appbar__title">{title}</div>
        <div className="appbar__subtitle">{subtitle}</div>
      </div>
      <span className="appbar__kill">
        <ConnectedKillSwitch variant="compact" />
      </span>
    </header>
  );
}

/** Phone app bar for pushed screens: back, title with segmented progress, key/value. */
export function DetailAppBar({
  back,
  title,
  titleMono,
  segments,
  metaKey,
  metaValue,
}: {
  back: { label: string; to: string };
  title: string;
  titleMono: string | null;
  segments: { total: number; current: number } | null;
  metaKey: string;
  metaValue: string;
}) {
  return (
    <header className="detailbar">
      <Link to={back.to} className="detailbar__back">
        <Icon name="chevron-left" size={22} />
        {back.label}
      </Link>
      <div className="detailbar__center">
        <div className="detailbar__title">
          {title}
          {titleMono ? <span className="mono detailbar__title-mono"> {titleMono}</span> : null}
        </div>
        {segments ? (
          <div
            className="detailbar__segments"
            role="img"
            aria-label={`${segments.total} onaydan ${segments.current}. sıradaki`}
          >
            {Array.from({ length: segments.total }, (_, index) => ({
              id: `seg-${index}`,
              done: index < segments.current,
            })).map((segment) => (
              <span
                key={segment.id}
                className={segment.done ? 'detailbar__seg detailbar__seg--done' : 'detailbar__seg'}
              />
            ))}
          </div>
        ) : null}
      </div>
      <div className="detailbar__meta">
        <div className="key ink-2">{metaKey}</div>
        <div className="detailbar__meta-value mono">{metaValue}</div>
      </div>
    </header>
  );
}

/** One line under the app bar: live, stale (approvals disabled) or a fault. */
export function StatusStrip({ to = '/ekip', target = 'Ekip ekranını aç' }: { to?: string; target?: string }) {
  const state = usePanel();
  const snapshot = state.snapshot;
  const health = snapshot ? systemHealth(snapshot) : { ok: true, text: 'Her şey yolunda' };
  if (state.connection === 'stale') {
    const at = state.lastHeartbeatAt ?? snapshot?.now ?? state.now;
    return (
      <div className="status status--stale" role="status">
        <LiveDot hollow />
        <span className="status__text">
          <span className="status__strong">Bağlantı yok</span> ·{' '}
          <span className="mono status__time--light">{formatClock(at)}</span> verisi · yeniden deneniyor
        </span>
      </div>
    );
  }
  const faulty = snapshot?.agents.find((agent) => agent.state === 'error' && agent.error);
  if (faulty?.error) {
    return (
      <Link to="/ekip" className="status status--fault">
        <svg width="12" height="12" viewBox="0 0 10 10" aria-hidden="true" focusable="false">
          <path d="M5 1L9.5 9H0.5Z" fill="currentColor" />
        </svg>
        <span className="status__text" aria-live="polite">
          {faulty.name} hata verdi · {faulty.error.message} ·{' '}
          <span className="mono">
            {sameDay(faulty.error.at, state.now)
              ? formatClock(faulty.error.at)
              : `${formatDayMonth(faulty.error.at)} ${formatClock(faulty.error.at)}`}
          </span>
        </span>
        <Icon name="chevron-right" size={16} className="status__chevron" />
      </Link>
    );
  }
  const connecting = state.connection === 'connecting';
  return (
    <Link
      to={to}
      className="status"
      aria-label={
        connecting
          ? `Bağlanıyor. ${target}`
          : `Durum: canlı, ${formatClock(state.now)}, ${health.text.toLocaleLowerCase('tr-TR')}${snapshot?.system.dryRun ? ', deneme modu açık: yürütücü dışarıya yazmaz' : ''}. ${target}`
      }
    >
      <LiveDot hollow={connecting} />
      <span className="status__text" aria-live="polite">
        {connecting ? (
          <span className="status__strong">Bağlanıyor…</span>
        ) : (
          <>
            <span className="status__strong">Canlı</span> <span className="mono">{formatClock(state.now)}</span>
            <span className={health.ok ? 'ink-2' : 'warning'}> · {health.text}</span>
            {snapshot?.system.dryRun ? <span className="warning"> · Deneme modu</span> : null}
          </>
        )}
      </span>
      <Icon name="chevron-right" size={16} className="status__chevron" />
    </Link>
  );
}

/**
 * Devam et after a stop: going back to running loosens the system, so it is a hold (like a
 * T4 approval) with the server's token asked when the hold starts. From a pause it is a tap.
 */
function ResumeHoldButton() {
  const store = useStore();
  const { holding, remaining, handlers } = useHold({
    durationMs: POLICY_HOLD_MS,
    disabled: false,
    onStart: () => store.beginResumeHold(),
    onComplete: () => void store.resumeTeam({ confirmedByHold: true }),
    onEarlyRelease: () => {
      store.cancelResumeHold();
      store.showToast('Durdurmadan sonra devam için 1,5 sn basılı tut', { icon: 'info' });
    },
  });
  return (
    <button
      type="button"
      className={holding ? 'banner__action banner__action--holding' : 'banner__action'}
      aria-label={
        holding
          ? `Devam et: basılı tutuluyor, ${formatSecondsLeft(remaining)} kaldı`
          : 'Devam et: durdurmadan sonra devam için 1,5 saniye basılı tut'
      }
      style={{ ['--hold-ms' as string]: `${POLICY_HOLD_MS}ms` }}
      {...handlers}
    >
      {holding ? `Basılı tut… ${formatSecondsLeft(remaining)}` : 'Devam et · basılı tut'}
    </button>
  );
}

/** Global banner for paused and stopped states; it replaces the status strip. */
export function Banner() {
  const store = useStore();
  const { team, snapshot } = usePanel();
  if (team.mode === 'running') return null;
  const stopped = team.mode === 'stopped';
  const at = team.since ? formatClock(team.since) : '';
  const waiting = snapshot?.executor.pending ?? 0;
  return (
    <div className={`banner banner--${stopped ? 'stopped' : 'paused'}`} role="status">
      {stopped ? <span className="banner__square" aria-hidden="true" /> : <Icon name="pause" size={16} />}
      <span className="banner__text">
        <span className="banner__title">
          {stopped ? 'Acil durduruldu' : 'Ekip duraklatıldı'} <span className="mono">{at}</span>
        </span>
        <span className="banner__sub">
          {stopped ? 'çalışan adımlar iptal · yürütücü durdu · kuyruk korunuyor' : 'kuyruk korunuyor · yürütücü durdu'}
          {waiting > 0 ? (
            <>
              {' · devam edince '}
              <span className="mono">{waiting}</span> onaylı iş uygulanır
            </>
          ) : null}
        </span>
      </span>
      {stopped ? (
        <ResumeHoldButton />
      ) : (
        <button type="button" className="banner__action" onClick={() => void store.resumeTeam()}>
          Devam et
        </button>
      )}
    </div>
  );
}

/** Status strip or banner, whichever applies. */
export function StatusArea(props: { to?: string; target?: string }) {
  const { team } = usePanel();
  return team.mode === 'running' ? <StatusStrip {...props} /> : <Banner />;
}

interface TabDef {
  id: TabId;
  label: string;
  icon: IconName;
  to: string;
}

const TABS: TabDef[] = [
  { id: 'bugun', label: 'Bugün', icon: 'sun', to: '/' },
  { id: 'onaylar', label: 'Onaylar', icon: 'square-check', to: '/onaylar' },
  { id: 'istekler', label: 'İstekler', icon: 'inbox', to: '/istekler' },
  { id: 'ekip', label: 'Ekip', icon: 'team', to: '/ekip' },
  { id: 'diger', label: 'Diğer', icon: 'layout-grid', to: '/diger' },
];

export function BottomTabBar({ active }: { active: TabId }) {
  const state = usePanel();
  const snapshot = state.snapshot;
  const c = snapshot ? counts(snapshot) : null;
  const team = snapshot ? agentCounts(snapshot.agents) : null;
  // Gönderilecekler lives under Diğer on the phone; its count rides on that tab (audit F3).
  const toSend = snapshot ? outboxWaiting(snapshot) : 0;
  const labelFor = (tab: TabDef): string | undefined => {
    if (!c || !team) return undefined;
    if (tab.id === 'diger' && toSend > 0) return `Diğer, ${toSend} gönderilecek`;
    if (tab.id === 'onaylar') return `Onaylar, ${c.approvals} bekliyor`;
    if (tab.id === 'istekler') return `İstekler, ${c.requests} açık`;
    if (tab.id === 'ekip') {
      return team.error > 0 ? `Ekip, ${team.error} hata` : `Ekip, ${team.working} ajan çalışıyor`;
    }
    return undefined;
  };
  return (
    <nav className="tabbar" aria-label="Ana gezinme">
      {TABS.map((tab) => {
        const isActive = tab.id === active;
        return (
          <Link
            key={tab.id}
            to={tab.to}
            className={isActive ? 'tabbar__item tabbar__item--active' : 'tabbar__item'}
            aria-current={isActive ? 'page' : undefined}
            aria-label={labelFor(tab)}
          >
            {isActive ? <span className="tabbar__bar" aria-hidden="true" /> : null}
            <span className="tabbar__icon">
              <Icon name={tab.icon} size={22} />
              {tab.id === 'onaylar' && c && c.approvals > 0 ? (
                <span className="badge badge--solid tabbar__badge" aria-hidden="true">
                  {c.approvals}
                </span>
              ) : null}
              {tab.id === 'istekler' && c && c.requests > 0 ? (
                <span className="badge badge--outline tabbar__badge" aria-hidden="true">
                  {c.requests}
                </span>
              ) : null}
              {tab.id === 'diger' && toSend > 0 ? (
                <span className="badge badge--outline tabbar__badge" aria-hidden="true">
                  {toSend}
                </span>
              ) : null}
              {tab.id === 'ekip' && team && (team.working > 0 || team.error > 0) ? (
                <span className={`tabbar__dot${team.error > 0 ? ' tabbar__dot--error' : ''}`} aria-hidden="true" />
              ) : null}
            </span>
            <span className="tabbar__label">{tab.label}</span>
          </Link>
        );
      })}
    </nav>
  );
}

function NavItem({
  to,
  icon,
  label,
  active,
  aria,
  right,
}: {
  to: string;
  icon: IconName;
  label: string;
  active: boolean;
  aria?: string | undefined;
  right?: ReactNode;
}) {
  return (
    <Link
      to={to}
      className={active ? 'nav-item nav-item--active' : 'nav-item'}
      aria-current={active ? 'page' : undefined}
      aria-label={aria}
    >
      <Icon name={icon} size={18} />
      {label}
      {right}
    </Link>
  );
}

/** The less used pages of a sidebar group; open while one of them is the current page. */
function MoreItems({ label, active, children }: { label: string; active: boolean; children: ReactNode }) {
  return (
    <details className="sidebar__more" open={active || undefined}>
      <summary className="sidebar__more-summary" aria-label={label}>
        <Icon name="chevron-down" size={16} />
        Daha fazla
      </summary>
      {children}
    </details>
  );
}

function ThemeToggle() {
  const preference = useThemePreference();
  const next = nextThemePreference(preference);
  return (
    <button
      type="button"
      className="icon-btn"
      aria-label={`Tema: ${THEME_LABELS[preference]}. ${THEME_LABELS[next]} yap`}
      title={`Tema: ${THEME_LABELS[preference]}`}
      onClick={() => setThemePreference(next)}
    >
      <Icon name="sun-moon" size={16} />
    </button>
  );
}

export function PolicyBox() {
  const { snapshot } = usePanel();
  if (!snapshot) return null;
  return (
    <section className="policy" aria-labelledby="sb-politika">
      <div className="policy__head">
        <h2 id="sb-politika" className="policy__title">
          Politika
        </h2>
        <Link to="/ayarlar/politika" className="policy__link">
          kurallar
        </Link>
      </div>
      <dl className="policy__rows">
        <div className="policy__row">
          <dt>Dış iletişim</dt>
          <dd>
            <TierBadge tier="T3" topic="DIŞ" variant="mini-tier" />
            onay
          </dd>
        </div>
        <div className="policy__row">
          <dt className="nowrap">Para, ürün</dt>
          <dd>
            <TierBadge tier="T4" topic="PARA" variant="mini-tier" />
            <span className="nowrap">her zaman</span>
          </dd>
        </div>
        <div className="policy__row">
          <dt>İndirim tavanı</dt>
          <dd className="policy__num mono">%{snapshot.policy.discountCapPct}</dd>
        </div>
        <div className="policy__row">
          <dt>Geri alma süresi</dt>
          <dd className="policy__num mono">{snapshot.policy.undoSeconds} sn</dd>
        </div>
      </dl>
    </section>
  );
}

/** The user menu of the sidebar: who is signed in, how, and logout for a session (ADR 0012). */
function SidebarUser() {
  const auth = useAuth();
  const name = auth?.session.human?.displayName ?? 'Sercan';
  const via = auth?.session.via ?? 'tailscale';
  const how = via === 'session' ? 'Oturum' : via === 'dev' ? 'Geliştirme' : 'Tailscale';
  return (
    <div className="sidebar__user">
      <span className="sidebar__avatar" aria-hidden="true">
        {name.slice(0, 1).toLocaleUpperCase('tr')}
      </span>
      <div className="sidebar__user-text">
        <div className="sidebar__user-name">{name}</div>
        <div className="sidebar__sub">Sahip · {how}</div>
      </div>
      <ThemeToggle />
      {auth !== null && via === 'session' ? (
        <button
          type="button"
          className="sidebar__logout"
          aria-label="Çıkış yap"
          title="Çıkış yap"
          onClick={() => void auth.logout()}
        >
          <Icon name="log-out" size={18} />
        </button>
      ) : null}
    </div>
  );
}

export function Sidebar({ route }: { route: Route }) {
  const state = usePanel();
  const snapshot = state.snapshot;
  const c = snapshot ? counts(snapshot) : null;
  const team = snapshot ? agentCounts(snapshot.agents) : null;
  const tab = activeTab(route);
  const section = route.name === 'section' ? route.section : null;
  const settingsActive = route.name === 'ayarlar' || route.name === 'baglanti';
  const setup = state.settings ? setupSummary(state.settings) : null;
  return (
    <aside className="sidebar" aria-label="Kenar çubuğu">
      <div className="sidebar__brand">
        <LogoTile />
        <div>
          <div className="sidebar__name">Twin</div>
          <div className="sidebar__sub">CadGaragePro · Etsy</div>
        </div>
      </div>
      {/*
        Three groups (panel audit 25 Oct 2026, F13): Karar is what waits for Sercan, Büyüme the business, Sistem the
        team and the settings. Each icon is used once; the less used pages of a group sit under "Daha fazla", which
        opens by itself when one of them is the current page. Every route stays where it was.
      */}
      <nav className="sidebar__nav" aria-label="Ana gezinme">
        <div className="sidebar__group key">Karar</div>
        <NavItem to="/" icon="sun" label="Bugün" active={tab === 'bugun'} />
        <NavItem to="/ozet" icon="layout-grid" label="Özet" active={section === 'ozet'} />
        <NavItem
          to="/onaylar"
          icon="square-check"
          label="Onaylar"
          active={tab === 'onaylar'}
          aria={c ? `Onaylar, ${c.approvals} bekliyor` : undefined}
          right={
            c && c.approvals > 0 ? <span className="badge badge--solid nav-item__right">{c.approvals}</span> : null
          }
        />
        <NavItem
          to="/gonderilecekler"
          icon="send"
          label="Gönderilecekler"
          active={section === 'gonderilecekler'}
          aria={snapshot ? `Gönderilecekler, ${outboxWaiting(snapshot)} bekliyor` : undefined}
          right={
            snapshot && outboxWaiting(snapshot) > 0 ? (
              <span className="badge badge--outline nav-item__right">{outboxWaiting(snapshot)}</span>
            ) : null
          }
        />
        <NavItem
          to="/istekler"
          icon="inbox"
          label="İstekler"
          active={tab === 'istekler'}
          aria={c ? `İstekler, ${c.requests} açık` : undefined}
          right={
            c && c.requests > 0 ? <span className="badge badge--outline nav-item__right">{c.requests}</span> : null
          }
        />
        <NavItem
          to="/sekreter"
          icon="briefcase"
          label="Sekreter"
          active={section === 'sekreter'}
          aria={snapshot ? `Sekreter, ${snapshot.attention.openSekreterItems} açık madde` : undefined}
          right={
            snapshot && snapshot.attention.openSekreterItems > 0 ? (
              <span className="nav-item__right nav-item__count mono">{snapshot.attention.openSekreterItems}</span>
            ) : null
          }
        />
        <div className="sidebar__group key">Büyüme</div>
        <NavItem to="/satislar" icon="receipt" label="Satışlar" active={section === 'satislar'} />
        <NavItem to="/satis-haritasi" icon="globe" label="Satış haritası" active={section === 'satis-haritasi'} />
        <NavItem
          to="/ilanlar"
          icon="tag"
          label="İlanlar"
          active={section === 'ilanlar'}
          aria={snapshot ? `İlanlar, ${snapshot.store.activeListings} aktif` : undefined}
          right={
            snapshot ? (
              <span className="nav-item__right nav-item__count mono">{snapshot.store.activeListings}</span>
            ) : null
          }
        />
        <NavItem to="/kanallar" icon="share" label="Kanallar" active={section === 'kanallar'} />
        <NavItem to="/seslendirme" icon="mic" label="Seslendirme" active={section === 'seslendirme'} />
        <NavItem to="/site" icon="activity" label="Site" active={section === 'site'} />
        <NavItem
          to="/site-sohbetleri"
          icon="message-square"
          label="Site sohbetleri"
          active={section === 'site-sohbetleri'}
        />
        <NavItem to="/seo" icon="search" label="SEO ve yapay zekâ" active={section === 'seo'} />
        <NavItem to="/leadler" icon="map-pin" label="Leadler" active={route.name === 'leadler'} />
        <NavItem to="/tanitim-takibi" icon="mail-check" label="Tanıtım takibi" active={section === 'tanitim-takibi'} />
        <MoreItems
          label="Büyüme: daha fazla"
          active={
            section === 'urunler' ||
            section === 'musteriler' ||
            section === 'potansiyel-musteriler' ||
            section === 'tanitim-gonderimi'
          }
        >
          <NavItem to="/urunler" icon="package" label="Ürünler" active={section === 'urunler'} />
          <NavItem to="/musteriler" icon="user-round" label="Müşteriler" active={section === 'musteriler'} />
          <NavItem
            to="/potansiyel-musteriler"
            icon="user-search"
            label="Potansiyel müşteriler"
            active={section === 'potansiyel-musteriler'}
          />
          <NavItem
            to="/tanitim-gonderimi"
            icon="mail"
            label="Tanıtım gönderimi"
            active={section === 'tanitim-gonderimi'}
          />
        </MoreItems>
        <div className="sidebar__group key">Sistem</div>
        <NavItem
          to="/ekip"
          icon="team"
          label="Ekip"
          active={tab === 'ekip'}
          aria={team ? `Ekip, ${team.working} ajan çalışıyor` : undefined}
          right={
            team ? (
              <span className="nav-item__right nav-item__live mono">
                {team.working > 0 ? <LiveDot /> : null}
                {team.working}
              </span>
            ) : null
          }
        />
        <NavItem to="/kayitlar" icon="file-text" label="Kayıtlar" active={section === 'kayitlar'} />
        <NavItem
          to="/ayarlar"
          icon="sliders"
          label="Ayarlar"
          active={settingsActive}
          aria={setup ? `Ayarlar, kurulum ${setup.ready} / ${setup.total} hazır` : undefined}
          right={
            setup && setup.ready < setup.total ? (
              <span className="nav-item__right nav-item__count mono">
                kurulum {setup.ready}/{setup.total}
              </span>
            ) : null
          }
        />
        <MoreItems
          label="Sistem: daha fazla"
          active={section === 'yapilanlar' || section === 'bilgi-bankasi' || section === 'gecmis-cevaplar'}
        >
          <NavItem to="/yapilanlar" icon="list-checks" label="Yapılanlar" active={section === 'yapilanlar'} />
          <NavItem to="/bilgi-bankasi" icon="book-open" label="Bilgi bankası" active={section === 'bilgi-bankasi'} />
          <NavItem
            to="/gecmis-cevaplar"
            icon="history"
            label="Geçmiş cevaplar"
            active={section === 'gecmis-cevaplar'}
          />
        </MoreItems>
      </nav>
      <PolicyBox />
      <SidebarUser />
    </aside>
  );
}

/** Desktop top bar: page title, date, one inline status line, stop controls on the right. */
export function TopBar({
  title,
  status,
  actions,
  subtitle,
}: {
  title: string;
  status: ReactNode;
  actions?: ReactNode;
  /** Replaces the date, for example "Kurulum, bağlantılar ve kurallar". */
  subtitle?: string;
}) {
  const state = usePanel();
  return (
    <header className="topbar">
      <div className="topbar__titles">
        <h1 className="topbar__title">{title}</h1>
        <span className="topbar__date">{subtitle ?? formatLongDate(state.now, true)}</span>
      </div>
      <div className="topbar__status">{status}</div>
      <div className="topbar__actions">
        {actions}
        <ConnectedKillSwitch variant="desktop" />
      </div>
    </header>
  );
}

/**
 * Inline desktop status for the top bar. Stale data shows the same warning as the phone strip. The health
 * words come from the snapshot (audit F5); `extra` adds the page's own count after them.
 */
export function TopBarStatus({ extra = '', to = '/ekip' }: { extra?: string; to?: string }) {
  const state = usePanel();
  const snapshot = state.snapshot;
  const health = snapshot ? systemHealth(snapshot) : null;
  if (state.team.mode !== 'running') return <Banner />;
  if (state.connection === 'stale') {
    const at = state.lastHeartbeatAt ?? state.now;
    return (
      <span className="topbar-status topbar-status--stale" role="status">
        <LiveDot hollow />
        <span>
          <span className="status__strong">Bağlantı yok</span> ·{' '}
          <span className="mono status__time--light">{formatClock(at)}</span> verisi · yeniden deneniyor · onaylar
          kapalı
        </span>
      </span>
    );
  }
  const words = [health?.text ?? '', extra].filter((part) => part !== '').join(' · ');
  const dry = snapshot?.system.dryRun === true;
  return (
    <Link
      to={to}
      className="topbar-status"
      title={`${words}${dry ? ' · Deneme modu' : ''}`}
      aria-label={`Canlı, ${formatClock(state.now)}. ${words}${dry ? ', deneme modu açık' : ''}. Ekip ekranını aç`}
    >
      <LiveDot />
      <span aria-live="polite" className="topbar-status__text">
        <span className="status__strong">Canlı</span>{' '}
        <span className="mono topbar-status__time">{formatClock(state.now)}</span>
        {health === null ? null : <span className={health.ok ? 'ink-2' : 'warning'}> · {health.text}</span>}
        {extra === '' ? null : <span className="ink-2"> · {extra}</span>}
        {dry ? <span className="warning"> · Deneme modu</span> : null}
      </span>
    </Link>
  );
}
